import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import OtpKeypad from './otp-keypad.component';
import useOtpRegistration, { BACKEND_OTP_LENGTH, OTP_DURATION_SECONDS } from './use-otp-registration';
import kdsLogo from '../../assets/images/IG_KDS.svg';
import './otp.styles.scss';

export default function OtpPage({ appName = 'kds', onVerify, onVerified, onContinue, onBack }) {
    const {
        clientCode, remaining, backendOtp, error, isSubmitting, isVerified,
        isInputDisabled, canSubmit, status, submitLabel, updateBackendOtp, submit
    } = useOtpRegistration({ appName, onVerify, onVerified, onContinue });
    const isDmb = appName === 'dmb';
    const product = isDmb ? 'IG Digital Menu Board' : 'IG Kitchen Display';
    const registrationArea = isDmb ? 'Digital Menu Board' : 'Kitchen';
    const enteredDigits = useRef([]);
    const [displayOtp, setDisplayOtp] = useState('');
    const [isPinVisible, setIsPinVisible] = useState(false);
    const inputRef = useRef(null);
    const verifyRef = useRef(null);
    const pendingCaret = useRef(null);
    const focusVerify = useRef(false);
    const [selection, setSelection] = useState({ start: 0, end: 0 });

    useLayoutEffect(() => {
        if (pendingCaret.current !== null) {
            const position = pendingCaret.current;
            pendingCaret.current = null;
            inputRef.current.setSelectionRange(position, position);
            if (focusVerify.current) {
                focusVerify.current = false;
                verifyRef.current.focus({ preventScroll: true });
            } else {
                inputRef.current.focus({ preventScroll: true });
            }
        }
    }, [backendOtp, selection]);

    const collapseSelection = (input) => {
        const position = input.selectionDirection === 'backward' ? input.selectionStart : input.selectionEnd;
        if (input.selectionStart !== input.selectionEnd) {
            input.setSelectionRange(position, position);
        }
        return position;
    };

    const trackSelection = (event) => {
        const position = collapseSelection(event.currentTarget);
        setSelection({ start: position, end: position });
    };

    const handleKeypadInput = (key) => {
        if (isInputDisabled) {
            return;
        }
        const start = collapseSelection(inputRef.current);
        let nextValue;
        let position;
        if (key === 'clear') {
            nextValue = '';
            position = 0;
        } else if (key === 'backspace') {
            position = Math.max(0, start - 1);
            nextValue = backendOtp.slice(0, position) + backendOtp.slice(start);
        } else {
            position = start + 1;
            nextValue = backendOtp.slice(0, start) + key + backendOtp.slice(start);
            if (nextValue.length > BACKEND_OTP_LENGTH) {
                return;
            }
        }
        pendingCaret.current = position;
        focusVerify.current = key !== 'clear' && key !== 'backspace' && nextValue.length === BACKEND_OTP_LENGTH;
        updateBackendOtp(nextValue);
        setSelection({ start: position, end: position });
        return nextValue;
    };

    const handleKeypadPress = (key) => {
        const previousValue = backendOtp;
        const nextValue = handleKeypadInput(key);
        if (/^[0-9]$/.test(key) && previousValue.length < BACKEND_OTP_LENGTH && nextValue?.length === BACKEND_OTP_LENGTH && (onVerify || onContinue)) {
            pendingCaret.current = null;
            focusVerify.current = false;
            submit(nextValue);
        }
    };

    useEffect(() => {
        if (!backendOtp) {
            setIsPinVisible(false);
        }
        const previous = enteredDigits.current;
        let prefix = 0;
        let suffix = 0;
        while (prefix < previous.length && prefix < backendOtp.length &&
            previous[prefix].digit === backendOtp[prefix]) {
            prefix++;
        }
        while (suffix < previous.length - prefix && suffix < backendOtp.length - prefix &&
            previous[previous.length - 1 - suffix].digit === backendOtp[backendOtp.length - 1 - suffix]) {
            suffix++;
        }
        // Keep existing deadlines when digits are inserted or deleted.
        const digits = [
            ...previous.slice(0, prefix),
            ...Array.from(backendOtp.slice(prefix, backendOtp.length - suffix),
                (digit) => ({ digit, hideAt: Date.now() + 1000 })),
            ...previous.slice(previous.length - suffix)
        ];
        enteredDigits.current = digits;
        let timer;
        const updateDisplay = () => {
            const now = Date.now();
            setDisplayOtp(digits.map(({ digit, hideAt }) => hideAt > now ? digit : '\u2022').join(''));
            const nextDeadline = Math.min(...digits.filter(({ hideAt }) => hideAt > now).map(({ hideAt }) => hideAt));
            if (Number.isFinite(nextDeadline)) {
                timer = setTimeout(updateDisplay, nextDeadline - now);
            }
        };
        updateDisplay();
        return () => clearTimeout(timer);
    }, [backendOtp]);

    const togglePinVisibility = () => {
        if (isPinVisible) {
            enteredDigits.current.forEach((entry) => { entry.hideAt = 0; });
            setDisplayOtp('\u2022'.repeat(backendOtp.length));
        }
        setIsPinVisible((visible) => !visible);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        submit();
    };

    return (
        <main className="otp-page" aria-label={`${product} registration`}>
            <header className="otp-header">
                <button className="otp-back" type="button" onClick={onBack}>
                    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                        <path d="M19 12H5M11 6l-6 6 6 6" />
                    </svg>
                    <span>Back</span>
                </button>
            </header>
            <div className="otp-content">
            <section className="otp-sign-in" aria-labelledby="otp-heading">
                <h1 id="otp-heading">Please sign in</h1>
                <div className="otp-product">
                    {isDmb ? <span className="otp-product-name">{product}</span> :
                        <img src={kdsLogo} alt={product} />}
                </div>
                <p>Visit <strong>Configuration Central/IGCM</strong> and enter this code</p>
                <div className="otp-code-row">
                <output className="otp-code" aria-label="Registration code" onCopy={(event) => event.preventDefault()}>{clientCode}</output>
                {!isVerified && (
                    <div className="auth-wrapper-circularbar">
                        <div
                            className="progress-bar"
                            style={{ '--custom-variable': remaining, '--otp-duration': OTP_DURATION_SECONDS }}
                            role="progressbar"
                            aria-label="OTP expiry countdown"
                            aria-valuemin={0}
                            aria-valuemax={OTP_DURATION_SECONDS}
                            aria-valuenow={remaining}
                            aria-valuetext={`${remaining} seconds remaining`}
                        />
                    </div>
                )}
                </div>
                <form className="otp-verification" onSubmit={handleSubmit} aria-busy={isSubmitting}>
                    <label htmlFor="backend-otp" className="font-weight-bold">Enter the 6-digit IGCM OTP</label>
                    <div className="otp-input">
                    <input
                        ref={inputRef}
                        id="backend-otp"
                        name="backendOtp"
                        type={isPinVisible ? 'text' : 'password'}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="Use the keypad to enter the OTP"
                        pattern="[0-9]{6}"
                        maxLength={BACKEND_OTP_LENGTH}
                        required
                        value={backendOtp}
                        disabled={isInputDisabled}
                        aria-invalid={Boolean(error)}
                        aria-describedby="otp-status"
                        onChange={(event) => {
                            updateBackendOtp(event.target.value);
                            trackSelection(event);
                        }}
                        onKeyUp={(event) => {
                            if (/^[0-9]$/.test(event.key) && event.currentTarget.value.length === BACKEND_OTP_LENGTH && (onVerify || onContinue)) {
                                submit();
                            }
                        }}
                        onSelect={trackSelection}
                        onKeyDown={(event) => {
                            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
                                event.preventDefault();
                            }
                        }}
                        onCopy={(event) => event.preventDefault()}
                        onCut={(event) => event.preventDefault()}
                        onDragStart={(event) => event.preventDefault()}
                        onPaste={(event) => event.preventDefault()}
                    />
                    <span className="otp-input-display" aria-hidden="true">
                        {Array.from(isPinVisible ? backendOtp : displayOtp, (digit, index) => <span key={index}>{digit}</span>)}
                    </span>
                    <button
                        className="otp-visibility-toggle"
                        type="button"
                        aria-label={isPinVisible ? 'Hide PIN' : 'Show PIN'}
                        title={isPinVisible ? 'Hide PIN' : 'Show PIN'}
                        aria-controls="backend-otp"
                        disabled={!backendOtp || isInputDisabled}
                        onClick={togglePinVisibility}
                    >
                        <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                            <path d="M2 12C6 4 18 4 22 12C18 20 6 20 2 12Z" />
                            <circle cx="12" cy="12" r="3" />
                            {!isPinVisible && <path d="M3 3l18 18" />}
                        </svg>
                    </button>
                    </div>
                    <p id="otp-status" className="otp-status" aria-label="Registration status" role={error ? 'alert' : 'status'}>{status}</p>
                    <OtpKeypad value={backendOtp} onKeyPress={handleKeypadPress} disabled={isInputDisabled} maxLength={BACKEND_OTP_LENGTH} selectionLength={selection.end - selection.start} />
                    <button ref={verifyRef} className="otp-verify" type="submit" disabled={!canSubmit} onClick={handleSubmit}>
                        {submitLabel}
                    </button>
                </form>
            </section>
            <section className="otp-instructions" aria-labelledby="otp-instructions-heading">
                <h2 id="otp-instructions-heading">Steps to register</h2>
                <ol>
                    <li>Log in to <strong>Configuration Central/IGCM</strong> and choose your respective store.</li>
                    <li>Navigate to <strong>{registrationArea} &rarr; Station</strong>.</li>
                    <li>Create a new entry or select the existing one you want to launch on this device.</li>
                    <li>Enter the 6-digit code shown here in the <strong>Registration</strong> field, then select <strong>Register</strong>.</li>
                    <li>Enter the 6-digit OTP returned by the IGCM and select <strong>Verify OTP</strong>.</li>
                </ol>
            </section>
            </div>
        </main>
    );
}